/**
 * Shared types + pure helpers for the dashboard filter panel.
 *
 * Extracted from DashboardFilters.tsx to keep that component under the 500-line
 * limit (CR-6352 review). Everything here is framework-free and unit-testable.
 */

import type { DashboardFilterState } from './types';

/**
 * The dashboard filter panel's filter object. The single source of truth is
 * `DashboardFilterState` in ./types; it's re-exported here under the
 * `FilterState` name the panel modules already use, so the two definitions
 * can't drift apart (CR-6352 review).
 */
export type FilterState = DashboardFilterState;

/**
 * CR-6401: filter keys that narrow the result set and so count toward the
 * "N filters active" badge. Dates are excluded because a range is always set
 * (the panel defaults to last 12 months), as are `currency` and `dataMode`,
 * which change how results are expressed rather than which rows are returned.
 */
const NARROWING_FILTER_KEYS: Array<keyof FilterState> = [
  'supplier', 'customer', 'site', 'supplierGroup', 'supplierType',
  'commodityGroup', 'commodityType', 'siteCountry', 'supplierCountry',
  'supplierCode', 'warehouse', 'division',
  'approvalStage', 'riskCategory',
];

/**
 * Count the filters currently narrowing the dashboard.
 *
 * @param filters - the panel's current filter state
 * @returns how many narrowing filters hold a non-default value
 */
export function countActiveFilters(filters: FilterState): number {
  const selected = NARROWING_FILTER_KEYS.filter((key) => {
    const value = filters[key];
    return typeof value === 'string' && value !== '';
  }).length;
  return selected + (filters.strategicOnly ? 1 : 0);
}

/** Human labels for the quick-range presets, used in the footer summary. */
const QUICK_RANGE_LABELS: Record<string, string> = {
  last12Months: 'last 12 months',
  thisMonth: 'this month',
  lastMonth: 'last month',
  lastQuarter: 'last quarter',
  thisYear: 'this year',
  lastYear: 'last year',
};

/**
 * Describe the active date range for the filter footer.
 *
 * @param activeQuickRange - the matched preset key, or null for a custom range
 * @param isLast12MonthsActive - whether the dates match the default range
 * @param filters - current filter state, used to render a custom range
 * @returns a phrase to follow "Showing results for "
 */
export function describeActiveRange(
  activeQuickRange: string | null,
  isLast12MonthsActive: boolean,
  filters: FilterState,
): string {
  if (isLast12MonthsActive) return QUICK_RANGE_LABELS['last12Months'];
  if (activeQuickRange && QUICK_RANGE_LABELS[activeQuickRange]) {
    return QUICK_RANGE_LABELS[activeQuickRange];
  }
  if (filters.dateFrom && filters.dateTo) return `${filters.dateFrom} to ${filters.dateTo}`;
  return 'all dates';
}

/** Format a Date as YYYY-MM-DD in local time (avoids UTC timezone shift). */
export function formatLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Computes the "Last 12 Months" date range: 12 complete months up to end of last month.
 * E.g. if today is April 9, 2026, returns { dateFrom: '2025-04-01', dateTo: '2026-03-31' }.
 */
/**
 * The `date_to` the dashboard actually queries. Historical mode (CR-4339)
 * excludes the current, incomplete month: the end is capped at the end of the
 * previous month (and defaults to it when no end is set). Live mode sends the
 * picker's end as-is. Every drill-down opened from a tile MUST use this same
 * value (METRIC-DEFINITIONS W3-T5): a ring frozen at 31 Aug and a grid that
 * runs to 30 Sep read two different arrears terms.
 *
 * @param dateTo - the picker's end date (ISO yyyy-mm-dd) or empty
 * @param dataMode - the Historical / Live switch
 * @param now - injectable clock for tests
 * @returns the ISO end date to send, or undefined when Live has no end set
 */
export function effectiveDateTo(
  dateTo: string | undefined,
  dataMode: 'historical' | 'live' | undefined,
  now: Date = new Date(),
): string | undefined {
  if (dataMode === 'live') return dateTo || undefined;
  const endOfPrevMonth = formatLocalDate(new Date(now.getFullYear(), now.getMonth(), 0));
  const userDateTo = dateTo || endOfPrevMonth;
  return userDateTo < endOfPrevMonth ? userDateTo : endOfPrevMonth;
}

/**
 * The mode-dependent query params a drill-down opened from a dashboard tile
 * must carry so it reads the SAME arrears term and the SAME window as the
 * tile (W3-T5): the capped `date_to` and, in Live mode, `data_mode=live`.
 * Spread it into the drill filters after `date_from`.
 *
 * @param filters - the dashboard filter state (or undefined before it loads)
 * @param now - injectable clock for tests
 * @returns `{ date_to?, data_mode? }`
 */
export function drilldownModeParams(
  filters: Pick<FilterState, 'dateTo' | 'dataMode'> | undefined,
  now: Date = new Date(),
): { date_to?: string; data_mode?: 'live' } {
  const dateTo = effectiveDateTo(filters?.dateTo, filters?.dataMode, now);
  return {
    ...(dateTo && { date_to: dateTo }),
    ...(filters?.dataMode === 'live' && { data_mode: 'live' as const }),
  };
}

export function getLast12MonthsRange(): { dateFrom: string; dateTo: string } {
  const today = new Date();
  const dateFrom = formatLocalDate(new Date(today.getFullYear() - 1, today.getMonth(), 1));
  const dateTo = formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 0));
  return { dateFrom, dateTo };
}

/** Resolve a named quick-range to its {dateFrom, dateTo} (null when unknown). */
export function getQuickRangeDates(range: string): { dateFrom: string; dateTo: string } | null {
  const today = new Date();
  switch (range) {
    case 'last12Months': return getLast12MonthsRange();
    case 'thisMonth': return {
      dateFrom: formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 1)),
      dateTo: formatLocalDate(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
    };
    case 'lastMonth': return {
      dateFrom: formatLocalDate(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
      dateTo: formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 0)),
    };
    case 'lastQuarter': {
      const lq = Math.floor(today.getMonth() / 3) - 1;
      const lqy = lq < 0 ? today.getFullYear() - 1 : today.getFullYear();
      const aq = lq < 0 ? 3 : lq;
      return {
        dateFrom: formatLocalDate(new Date(lqy, aq * 3, 1)),
        dateTo: formatLocalDate(new Date(lqy, (aq + 1) * 3, 0)),
      };
    }
    case 'thisYear': return {
      dateFrom: formatLocalDate(new Date(today.getFullYear(), 0, 1)),
      dateTo: formatLocalDate(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
    };
    case 'lastYear': return {
      dateFrom: formatLocalDate(new Date(today.getFullYear() - 1, 0, 1)),
      dateTo: formatLocalDate(new Date(today.getFullYear() - 1, 11, 31)),
    };
    default: return null;
  }
}

/**
 * Drill filters for the Group Detail KPI tab (W3-T5 + R-158). The drill API
 * reads `supplier` / `customer` / `site` — the tab used to send `supplier_id`
 * / `customer_id` / `site_id`, which the API dropped, so its OTIF drill was
 * unscoped. A clicked site bar scopes the drill to that site's `customer`
 * (the bar is one customer site), overriding the panel's customer filter.
 */
export function groupDetailDrillFilters(
  filters: (Pick<FilterState, 'dateFrom' | 'dateTo' | 'dataMode' | 'supplier' | 'supplierGroup'
    | 'customer' | 'site' | 'siteCountry' | 'supplierCountry'>
    // Optional: the group tiles carry a warehouse scope, the callers that
    // predate CR-7229 do not.
    & Partial<Pick<FilterState, 'warehouse' | 'supplierType'>>) | undefined,
  clickedCustomerId?: number | null,
  now: Date = new Date(),
): Record<string, string> {
  const out: Record<string, string> = {};
  if (!filters) {
    if (clickedCustomerId != null) out['customer'] = String(clickedCustomerId);
    return out;
  }
  if (filters.dateFrom) out['date_from'] = filters.dateFrom;
  const mode = drilldownModeParams(filters, now);
  if (mode.date_to) out['date_to'] = mode.date_to;
  if (mode.data_mode) out['data_mode'] = mode.data_mode;
  if (filters.supplier) out['supplier'] = String(filters.supplier);
  if (filters.supplierGroup) out['supplier_group'] = String(filters.supplierGroup);
  const customer = clickedCustomerId != null ? String(clickedCustomerId) : (filters.customer ? String(filters.customer) : '');
  if (customer) out['customer'] = customer;
  if (filters.site) out['site'] = String(filters.site);
  // CR-7229: the warehouse scope has to reach the drill-down too, or the
  // detail list shows rows the tile it was opened from excluded.
  if (filters.warehouse) out['warehouse'] = String(filters.warehouse);
  if (filters.siteCountry) out['site_country'] = filters.siteCountry;
  if (filters.supplierCountry) out['supplier_country'] = filters.supplierCountry;
  // Customer review 11 Sep (item 34): Internal / External reaches the drills.
  if (filters.supplierType) out['supplier_type'] = filters.supplierType;
  return out;
}

/**
 * Compute the date range + dataMode a quick-range button should apply.
 * "thisMonth" is the only Live range; every other range is Historical.
 * Returns null for an unknown range name.
 */
export function computeQuickDateRange(
  range: string,
): { dateFrom: string; dateTo: string; dataMode: 'historical' | 'live' } | null {
  const today = new Date();
  if (range === 'thisMonth') {
    return {
      dateFrom: formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 1)),
      dateTo: formatLocalDate(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
      dataMode: 'live',
    };
  }
  let dateFrom = '';
  let dateTo = '';
  switch (range) {
    case 'lastMonth':
      dateFrom = formatLocalDate(new Date(today.getFullYear(), today.getMonth() - 1, 1));
      dateTo = formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 0));
      break;
    case 'thisQuarter': {
      const currentQuarter = Math.floor(today.getMonth() / 3);
      dateFrom = formatLocalDate(new Date(today.getFullYear(), currentQuarter * 3, 1));
      dateTo = formatLocalDate(new Date(today.getFullYear(), (currentQuarter + 1) * 3, 0));
      break;
    }
    case 'lastQuarter': {
      const lastQuarter = Math.floor(today.getMonth() / 3) - 1;
      const lastQuarterYear = lastQuarter < 0 ? today.getFullYear() - 1 : today.getFullYear();
      const adjustedQuarter = lastQuarter < 0 ? 3 : lastQuarter;
      dateFrom = formatLocalDate(new Date(lastQuarterYear, adjustedQuarter * 3, 1));
      dateTo = formatLocalDate(new Date(lastQuarterYear, (adjustedQuarter + 1) * 3, 0));
      break;
    }
    case 'thisYear':
      dateFrom = formatLocalDate(new Date(today.getFullYear(), 0, 1));
      dateTo = formatLocalDate(new Date(today.getFullYear(), today.getMonth() + 1, 0));
      break;
    case 'lastYear':
      dateFrom = formatLocalDate(new Date(today.getFullYear() - 1, 0, 1));
      dateTo = formatLocalDate(new Date(today.getFullYear() - 1, 11, 31));
      break;
    case 'last12Months': {
      const r = getLast12MonthsRange();
      dateFrom = r.dateFrom;
      dateTo = r.dateTo;
      break;
    }
    default:
      return null;
  }
  return { dateFrom, dateTo, dataMode: 'historical' };
}

/** Map of ISO country codes to display names (Company Country shows ISO codes). */
export const countryDisplayNames: Record<string, string> = {
  GB: 'United Kingdom', US: 'United States', DE: 'Germany', FR: 'France',
  CN: 'China', IN: 'India', JP: 'Japan', CA: 'Canada', AU: 'Australia',
  IT: 'Italy', ES: 'Spain', NL: 'Netherlands', SE: 'Sweden', CH: 'Switzerland',
  BR: 'Brazil', MX: 'Mexico', KR: 'South Korea', SG: 'Singapore',
  IE: 'Ireland', PL: 'Poland', BE: 'Belgium', AT: 'Austria', DK: 'Denmark',
  NO: 'Norway', FI: 'Finland', PT: 'Portugal', CZ: 'Czech Republic',
  NZ: 'New Zealand', ZA: 'South Africa', TH: 'Thailand', MY: 'Malaysia',
  TW: 'Taiwan', HK: 'Hong Kong', AE: 'United Arab Emirates', SA: 'Saudi Arabia',
  TR: 'Turkey', RO: 'Romania', HU: 'Hungary', PH: 'Philippines', VN: 'Vietnam',
};

/**
 * Derive the sorted, de-duplicated set of country values from a list of records,
 * ordered by display name. Works for both Company Country (ISO codes translated
 * via countryDisplayNames) and Supplier Country (free-text country names that
 * fall through to themselves).
 *
 * @param items - Records each optionally carrying a `country` value (ISO code
 *   or free-text name); blank/nullish countries are ignored.
 * @returns The unique country values, sorted by their display name.
 */
export function deriveCountryOptions(items: Array<{ country?: string | null }>): string[] {
  // Type guard (instead of an `as string[]` cast) so the compiler keeps the
  // narrowed `string` type and would flag any future change to the input shape.
  const countries = items
    .map((s) => s.country)
    .filter((c): c is string => Boolean(c));
  return Array.from(new Set(countries)).sort((a, b) => {
    const nameA = countryDisplayNames[a] || a;
    const nameB = countryDisplayNames[b] || b;
    return nameA.localeCompare(nameB);
  });
}
